// ============================================================================
// SupportInboxPage.tsx — ЭКРАН «ПОДДЕРЖКА» (§3.4): очередь обращений и карточка
// обращения. Режим определяется ролью (рендерер приложения передаёт isStaff):
//   • специалист поддержки/админ — полный инбокс (фильтры статус/назначение/канал/
//     поиск, пагинация, назначение/статусы/приоритет/ответы/шаблоны);
//   • обычный пользователь — «Мои обращения» (сервер отдаёт только его обращения,
//     управляющие действия скрыты, доступно «Открыть чат» с «Поддержкой»).
// Live-обновление — события шины support.ticket.* (единый eventBusHub, без опроса).
// ============================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import type { SupportTicket } from '@zdc/sdk';
import { getSDK } from '@/store/sdkStore';
import { useToastStore } from '@/store/toastStore';
import { Button } from '@/components/ui/button';
import { HelpHint } from '@/components/ui/HelpHint';
import { eventBusHub } from '@/lib/eventBusHub';
import { openConversation } from '@/lib/chatNavigation';
import { isNotFoundError, logErrorToJournal, logSwallowed } from '@/lib/errors';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import {
  EMPTY_TICKET_FILTER,
  TICKET_CHANNEL_OPTIONS,
  TICKET_PAGE_SIZE,
  TICKET_SCOPE_OPTIONS,
  TICKET_STATUS_OPTIONS,
  isSupportTicketEvent,
  supportErrorText,
  ticketIdFromEventPayload,
  type SupportTicketFilter,
} from '@/lib/supportInbox';
import { SupportTicketCard } from '@/components/support/SupportTicketCard';
import { SupportTicketDetails } from '@/components/support/SupportTicketDetails';

interface Props {
  /** Специалист поддержки/админ (SSoT прав — сервер; здесь UX-режим экрана). */
  isStaff: boolean;
  /**
   * Явный выбор области «Мои обращения / Все обращения» вместо выпадающего
   * списка назначений. Используется вкладкой «Поддержка» приложения «Кабинет»;
   * обычный инбокс («Поддержка») оставляет привычный селект.
   */
  scopeSwitch?: boolean;
}

export function SupportInboxPage({ isStaff, scopeSwitch = false }: Props) {
  const showError = useToastStore((s) => s.showError);
  const { isMobile } = useBreakpoint();

  const [filter, setFilter] = useState<SupportTicketFilter>(EMPTY_TICKET_FILTER);
  const [searchDraft, setSearchDraft] = useState('');
  const [items, setItems] = useState<SupportTicket[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const [selectedId, setSelectedId] = useState('');
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [ticketLoading, setTicketLoading] = useState(false);
  const [ticketError, setTicketError] = useState('');

  const load = useCallback(async (silent: boolean) => {
    if (!silent) setLoading(true);
    try {
      const r = await getSDK().support.listTickets({
        status: filter.status,
        assigned: filter.assigned,
        channel: filter.channel,
        search: filter.search,
        pageSize: TICKET_PAGE_SIZE,
        offset,
      });
      setItems(r.items);
      setTotal(r.total);
      setError('');
    } catch (e) {
      if (silent) {
        logSwallowed(e, 'pages/SupportInboxPage.tsx:очередь (фон)');
      } else {
        const message = supportErrorText(e);
        setError(message);
        showError(message);
      }
    } finally {
      if (!silent) setLoading(false);
      setRefreshing(false);
    }
  }, [filter, offset, showError]);

  const loadTicket = useCallback(async (id: string, silent: boolean) => {
    if (!silent) setTicketLoading(true);
    try {
      const t = await getSDK().support.getTicket({ ticketId: id });
      setSelectedTicket(t);
      setTicketError('');
    } catch (e) {
      if (isNotFoundError(e)) {
        setSelectedTicket(null);
        setTicketError('Обращение не найдено (возможно, удалено).');
      } else {
        logErrorToJournal(e, 'pages/SupportInboxPage.tsx:карточка обращения');
        setTicketError(supportErrorText(e));
      }
    } finally {
      if (!silent) setTicketLoading(false);
    }
  }, []);

  // Живое обновление: шина support.ticket.* (создание/обновление обращения).
  const loadRef = useRef(load);
  useEffect(() => { loadRef.current = load; }, [load]);
  const loadTicketRef = useRef(loadTicket);
  useEffect(() => { loadTicketRef.current = loadTicket; }, [loadTicket]);
  const selectedIdRef = useRef(selectedId);
  useEffect(() => { selectedIdRef.current = selectedId; }, [selectedId]);
  useEffect(() => eventBusHub.subscribe((ev) => {
    if (!isSupportTicketEvent(ev.topic)) return;
    void loadRef.current(true);
    const id = ticketIdFromEventPayload(ev.payload);
    if (id && id === selectedIdRef.current) {
      void loadTicketRef.current(id, true);
    }
  }), []);
  useEffect(() => { void load(false); }, [load]);

  const refreshAfterAction = useCallback(() => {
    void loadRef.current(true);
    if (selectedIdRef.current) {
      void loadTicketRef.current(selectedIdRef.current, true);
    }
  }, []);

  const selectTicket = (id: string) => {
    setSelectedId(id);
    setTicketError('');
    setSelectedTicket(null);
    void loadTicket(id, false);
  };

  const applySearch = () => {
    setFilter((f) => (f.search === searchDraft ? f : { ...f, search: searchDraft }));
    setOffset(0);
  };

  const changeFilter = (patch: Partial<SupportTicketFilter>) => {
    setFilter((f) => ({ ...f, ...patch }));
    setOffset(0);
  };

  const openTicketChat = (t: SupportTicket) => {
    if (!t.conversationId) {
      showError('Чат обращения недоступен.');
      return;
    }
    openConversation(t.conversationId);
  };

  const from = total === 0 ? 0 : offset + 1;
  const to = offset + items.length;
  const hasPrev = offset > 0;
  const hasNext = offset + TICKET_PAGE_SIZE < total;
  const selectClass = 'h-8 rounded-md border bg-transparent px-2 text-sm';
  const title = isStaff ? 'Поддержка' : 'Мои обращения';

  const listPane = useMemo(() => (
    <div className="flex h-full min-h-0 flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <select className={selectClass} value={filter.status} onChange={(e) => changeFilter({ status: e.target.value })}>
          <option value="">Все статусы</option>
          {TICKET_STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        {isStaff && (scopeSwitch ? (
          <div
            className="inline-flex overflow-hidden rounded-md border"
            role="group"
            aria-label="Область обращений"
          >
            {TICKET_SCOPE_OPTIONS.map((o) => (
              <button
                key={o.value || 'all'}
                type="button"
                onClick={() => changeFilter({ assigned: o.value })}
                aria-pressed={filter.assigned === o.value}
                className={`h-8 px-2.5 text-[12px] transition-colors ${
                  filter.assigned === o.value
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-accent/60'
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        ) : (
          <select className={selectClass} value={filter.assigned} onChange={(e) => changeFilter({ assigned: e.target.value })}>
            <option value="">Все назначения</option>
            <option value="me">Мои</option>
            <option value="none">Не назначены</option>
          </select>
        ))}
        <select className={selectClass} value={filter.channel} onChange={(e) => changeFilter({ channel: e.target.value })}>
          <option value="">Все каналы</option>
          {TICKET_CHANNEL_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      <form
        className="flex items-center gap-2"
        onSubmit={(e) => { e.preventDefault(); applySearch(); }}
      >
        <input
          className="h-8 min-w-0 flex-1 rounded-md border bg-transparent px-2 text-sm"
          placeholder="Поиск по теме…"
          value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
        />
        <Button size="sm" variant="outline" className="h-8 text-[11px]" type="submit">Найти</Button>
      </form>

      {error && !loading && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          <span className="min-w-0 flex-1">{error}</span>
          <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => void load(false)}>
            Повторить
          </Button>
        </div>
      )}

      {loading && items.length === 0 && (
        <div className="text-sm text-muted-foreground">Загрузка обращений…</div>
      )}

      {!loading && !error && items.length === 0 && (
        <div className="space-y-1 text-sm text-muted-foreground">
          <div>{isStaff ? 'Обращений с выбранными условиями нет.' : 'У вас пока нет обращений.'}</div>
          {!isStaff && (
            <div className="text-[12px]">
              Напишите в личный чат «Поддержка» — обращение появится здесь автоматически.
            </div>
          )}
        </div>
      )}

      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto">
        {items.map((t) => (
          <SupportTicketCard
            key={t.id}
            ticket={t}
            selected={t.id === selectedId}
            onSelect={() => selectTicket(t.id)}
          />
        ))}
      </div>

      {total > TICKET_PAGE_SIZE && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-2 text-[11px] text-muted-foreground">
          <span>Показаны {from}–{to} из {total}</span>
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-[11px]"
              disabled={!hasPrev}
              onClick={() => setOffset(Math.max(0, offset - TICKET_PAGE_SIZE))}
            >
              Назад
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-[11px]"
              disabled={!hasNext}
              onClick={() => setOffset(offset + TICKET_PAGE_SIZE)}
            >
              Вперёд
            </Button>
          </div>
        </div>
      )}
    </div>
  ), [filter, searchDraft, error, loading, items, selectedId, total, offset, isStaff, scopeSwitch, from, to, hasPrev, hasNext, load]);

  const detailsPane = (
    <div className="flex min-h-0 flex-col gap-2">
      {ticketLoading && <div className="text-sm text-muted-foreground">Загрузка обращения…</div>}
      {!ticketLoading && ticketError && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          <span className="min-w-0 flex-1">{ticketError}</span>
          {selectedId && (
            <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => void loadTicket(selectedId, false)}>
              Повторить
            </Button>
          )}
        </div>
      )}
      {!ticketLoading && !ticketError && selectedTicket && (
        <SupportTicketDetails
          ticket={selectedTicket}
          isStaff={isStaff}
          onChanged={refreshAfterAction}
          onOpenChat={() => openTicketChat(selectedTicket)}
        />
      )}
      {!ticketLoading && !ticketError && !selectedTicket && (
        <div className="text-sm text-muted-foreground">Выберите обращение из списка.</div>
      )}
    </div>
  );

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 overflow-hidden p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1 text-sm font-semibold">
          {title}
          <HelpHint
            title="Обращения поддержки"
            text={
              isStaff
                ? 'Очередь обращений пользователей к «Поддержке»: один личный чат пользователя с ботом «Поддержка» — одно обращение. Назначение переводит новое обращение в «В работе»; ответ уходит пользователю в его чат.'
                : 'Здесь видны ваши обращения к «Поддержке». Ответы приходят в личный чат с «Поддержкой»; новое сообщение в чате обновляет обращение (решённое открывается заново).'
            }
          />
        </div>
        <Button size="sm" variant="outline" onClick={() => { setRefreshing(true); void load(true); }} disabled={refreshing}>
          <RefreshCw className={`mr-1 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />Обновить
        </Button>
      </div>

      {isMobile ? (
        selectedId ? (
          <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">
            <Button size="sm" variant="ghost" className="h-7 w-fit text-[11px]" onClick={() => setSelectedId('')}>
              ← К списку обращений
            </Button>
            {detailsPane}
          </div>
        ) : (
          <div className="min-h-0 flex-1">{listPane}</div>
        )
      ) : (
        <div className="flex min-h-0 flex-1 gap-3">
          <div className="flex w-[380px] shrink-0 flex-col overflow-hidden rounded-lg border p-2">
            {listPane}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto rounded-lg border p-3">
            {detailsPane}
          </div>
        </div>
      )}
    </div>
  );
}
